import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  FunnelChart, Funnel, LabelList,
} from 'recharts';
import { analyticsApi } from '../api/analyticsApi';
import {
  Users, Activity, Clock, TrendingUp, ArrowLeft,
  RefreshCw, Calendar, ChevronDown, BarChart3, Search, FileText, LogIn,
  HelpCircle, AlertTriangle, GitBranch, Tag, Shield
} from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const FEATURE_LABELS = {
  fact_finder: 'Fact Finder',
  job_briefing: 'Job Briefing',
  ask_stewie: 'Ask Stewie',
  ai_assistant: 'AI Assistant',
  home: 'Home Page',
  login: 'Login',
};

function formatDuration(seconds) {
  if (!seconds) return '0s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function formatDate(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoStr;
  }
}

function StatCard({ icon: Icon, label, value, sub, color = '#3b82f6' }) {
  return (
    <div className="stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="stat-icon" style={{ backgroundColor: `${color}18`, color }}>
        <Icon size={22} />
      </div>
      <div className="stat-info">
        <span className="stat-value">{value}</span>
        <span className="stat-label">{label}</span>
        {sub && <span className="stat-sub">{sub}</span>}
      </div>
    </div>
  );
}

export default function AdminAnalyticsDashboard({ user }) {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [featureUsage, setFeatureUsage] = useState([]);
  const [dailyTrends, setDailyTrends] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [contentQuality, setContentQuality] = useState(null);
  const [sessionStats, setSessionStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [topQuestions, setTopQuestions] = useState([]);
  const [weakAnswers, setWeakAnswers] = useState([]);
  const [featureDropoff, setFeatureDropoff] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, fuRes, dtRes, tuRes, reRes, etRes, cqRes, ssRes, tqRes, waRes, fdRes, cbRes] = await Promise.all([
        analyticsApi.getOverview(days),
        analyticsApi.getFeatureUsage(days),
        analyticsApi.getDailyTrends(Math.min(days, 90)),
        analyticsApi.getTopUsers(days),
        analyticsApi.getRecentEvents(50),
        analyticsApi.getEventTypes(days),
        analyticsApi.getContentQuality(days),
        analyticsApi.getSessionStats(days),
        analyticsApi.getTopQuestions(days).catch(() => ({ data: [] })),
        analyticsApi.getWeakAnswers(days).catch(() => ({ data: [] })),
        analyticsApi.getFeatureDropoff(days).catch(() => ({ data: [] })),
        analyticsApi.getCategoryBreakdown(days).catch(() => ({ data: [] })),
      ]);
      setOverview(ovRes.data);
      setFeatureUsage(fuRes.data);
      setDailyTrends(dtRes.data);
      setTopUsers(tuRes.data);
      setRecentEvents(reRes.data);
      setEventTypes(etRes.data);
      setContentQuality(cqRes.data);
      setSessionStats(ssRes.data);
      setTopQuestions(tqRes.data);
      setWeakAnswers(waRes.data);
      setFeatureDropoff(fdRes.data);
      setCategoryBreakdown(cbRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pieData = featureUsage.map((f) => ({
    name: FEATURE_LABELS[f.feature] || f.feature,
    value: f.count,
  }));

  return (
    <div className="analytics-dash" data-testid="admin-analytics-dashboard">
      <style>{dashStyles}</style>

      {/* Header */}
      <header className="ad-header">
        <div className="ad-header-left">
          <button className="ad-back" onClick={() => navigate('/')} data-testid="back-btn">
            <ArrowLeft size={18} /> Back
          </button>
          <img src="/stewie_front.png" alt="Stewie" data-testid="stewie-logo" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center 25%', border: '2px solid #334155' }} />
          <div>
            <h1 className="ad-title">Analytics Dashboard</h1>
            <p className="ad-subtitle">Monitor app usage, feature adoption & user activity</p>
          </div>
        </div>
        <div className="ad-header-right">
          <div className="ad-range-select">
            <Calendar size={14} />
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="days-select">
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <ChevronDown size={14} />
          </div>
          <button className="ad-refresh" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw size={16} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="ad-refresh" onClick={() => navigate('/admin/grievance-intelligence')} data-testid="case-intel-link" style={{ background: '#7c3aed' }}>
            <Shield size={16} /> Case Intel
          </button>
          <button className="ad-refresh" onClick={() => navigate('/admin/document-governance')} data-testid="doc-governance-link" style={{ background: '#0891b2' }}>
            <FileText size={16} /> Doc Governance
          </button>
          <button className="ad-refresh" onClick={() => navigate('/admin/review-queue')} data-testid="review-queue-link" style={{ background: '#dc2626' }}>
            <AlertTriangle size={16} /> Review Queue
          </button>
        </div>
      </header>

      {loading && !overview ? (
        <div className="ad-loading" data-testid="loading-state">
          <RefreshCw size={32} className="spin" />
          <p>Loading analytics...</p>
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="ad-stats-grid">
            <StatCard icon={Users} label="Total Users" value={overview?.totalUsers ?? 0} color="#3b82f6" />
            <StatCard icon={Activity} label="Active Today" value={overview?.activeUsersToday ?? 0} color="#10b981" />
            <StatCard icon={BarChart3} label="Total Events" value={overview?.totalEvents ?? 0} sub={`${days}d`} color="#f59e0b" />
            <StatCard icon={Clock} label="Avg Session" value={formatDuration(overview?.avgSessionDuration)} color="#8b5cf6" />
            <StatCard icon={TrendingUp} label="Top Feature" value={FEATURE_LABELS[overview?.topFeature] || overview?.topFeature || 'N/A'} color="#ec4899" />
            <StatCard icon={Search} label="Searches" value={contentQuality?.searchCount ?? 0} sub={`${days}d`} color="#06b6d4" />
            <StatCard icon={FileText} label="Cases Created" value={contentQuality?.totalCases ?? 0} sub={`${days}d`} color="#14b8a6" />
            <StatCard icon={LogIn} label="Logins" value={contentQuality?.loginCount ?? 0} sub={`${days}d`} color="#f97316" />
          </div>

          {/* Tabs */}
          <div className="ad-tabs">
            {['overview', 'engagement', 'insights', 'users', 'events'].map((t) => (
              <button
                key={t}
                className={`ad-tab ${activeTab === t ? 'active' : ''}`}
                onClick={() => setActiveTab(t)}
                data-testid={`tab-${t}`}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="ad-grid-2">
              {/* Daily Trends Chart */}
              <div className="ad-card ad-card-wide" data-testid="daily-trends-chart">
                <h3 className="ad-card-title">Daily Activity</h3>
                {dailyTrends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={dailyTrends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                      <Legend />
                      <Line type="monotone" dataKey="events" stroke="#3b82f6" strokeWidth={2} dot={false} name="Events" />
                      <Line type="monotone" dataKey="sessions" stroke="#10b981" strokeWidth={2} dot={false} name="Sessions" />
                      <Line type="monotone" dataKey="uniqueUsers" stroke="#f59e0b" strokeWidth={2} dot={false} name="Unique Users" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="ad-empty">No trend data yet. Events will appear as users interact with the app.</div>
                )}
              </div>

              {/* Feature Usage Pie */}
              <div className="ad-card" data-testid="feature-usage-chart">
                <h3 className="ad-card-title">Feature Usage</h3>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="ad-empty">No feature usage data yet.</div>
                )}
              </div>

              {/* Event Type Breakdown */}
              <div className="ad-card" data-testid="event-types-chart">
                <h3 className="ad-card-title">Event Types</h3>
                {eventTypes.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={eventTypes.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <YAxis dataKey="eventType" type="category" width={100} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                      <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="ad-empty">No event type data yet.</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'engagement' && (
            <div className="ad-grid-2">
              {/* Session Stats */}
              <div className="ad-card" data-testid="session-stats-card">
                <h3 className="ad-card-title">Session Statistics</h3>
                {sessionStats ? (
                  <div className="ad-kv-grid">
                    <div className="ad-kv"><span className="ad-kv-label">Total Sessions</span><span className="ad-kv-value">{sessionStats.totalSessions}</span></div>
                    <div className="ad-kv"><span className="ad-kv-label">Completed</span><span className="ad-kv-value">{sessionStats.completedSessions}</span></div>
                    <div className="ad-kv"><span className="ad-kv-label">Avg Duration</span><span className="ad-kv-value">{formatDuration(sessionStats.avgDuration)}</span></div>
                    <div className="ad-kv"><span className="ad-kv-label">Max Duration</span><span className="ad-kv-value">{formatDuration(sessionStats.maxDuration)}</span></div>
                    <div className="ad-kv"><span className="ad-kv-label">Avg Sessions/User</span><span className="ad-kv-value">{sessionStats.avgSessionsPerUser}</span></div>
                  </div>
                ) : (
                  <div className="ad-empty">No session data yet.</div>
                )}
              </div>

              {/* Top Actions */}
              <div className="ad-card" data-testid="top-actions-card">
                <h3 className="ad-card-title">Top Actions</h3>
                {contentQuality?.topActions?.length > 0 ? (
                  <div className="ad-table-wrap">
                    <table className="ad-table">
                      <thead>
                        <tr><th>Feature</th><th>Action</th><th>Count</th></tr>
                      </thead>
                      <tbody>
                        {contentQuality.topActions.map((a, i) => (
                          <tr key={i}>
                            <td>{FEATURE_LABELS[a.feature] || a.feature}</td>
                            <td><span className="ad-event-type">{a.action}</span></td>
                            <td><span className="ad-badge">{a.count}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="ad-empty">No action data yet.</div>
                )}
              </div>

              {/* Cases by Type */}
              <div className="ad-card" data-testid="cases-by-type-card">
                <h3 className="ad-card-title">Cases Created by Type</h3>
                {contentQuality?.casesByType?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={contentQuality.casesByType}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="type" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                      <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="ad-empty">No cases created yet.</div>
                )}
              </div>

              {/* Feature Usage Detail */}
              <div className="ad-card" data-testid="feature-detail-card">
                <h3 className="ad-card-title">Feature Usage Detail</h3>
                {featureUsage.length > 0 ? (
                  <div className="ad-table-wrap">
                    <table className="ad-table">
                      <thead>
                        <tr><th>Feature</th><th>Events</th><th>Share</th></tr>
                      </thead>
                      <tbody>
                        {featureUsage.map((f, i) => (
                          <tr key={i}>
                            <td>{FEATURE_LABELS[f.feature] || f.feature}</td>
                            <td><span className="ad-badge">{f.count}</span></td>
                            <td>
                              <div className="ad-bar-wrap">
                                <div className="ad-bar" style={{ width: `${f.percentage}%`, background: COLORS[i % COLORS.length] }} />
                                <span>{f.percentage}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="ad-empty">No feature data yet.</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'insights' && (
            <div className="ad-grid-2">
              {/* Top Questions */}
              <div className="ad-card ad-card-wide" data-testid="top-questions-table">
                <h3 className="ad-card-title"><HelpCircle size={16} style={{display:'inline',marginRight:6,verticalAlign:'text-bottom'}} />Top Questions Asked</h3>
                {topQuestions.length > 0 ? (
                  <div className="ad-table-wrap">
                    <table className="ad-table">
                      <thead>
                        <tr><th>Question</th><th>Count</th><th>Category</th><th>Last Asked</th></tr>
                      </thead>
                      <tbody>
                        {topQuestions.map((q, i) => (
                          <tr key={i}>
                            <td style={{maxWidth:360,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{q.questionText}</td>
                            <td><span className="ad-badge">{q.count}</span></td>
                            <td><span className="ad-cat-badge">{q.category}</span></td>
                            <td className="ad-time">{formatDate(q.lastAsked)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="ad-empty">No question data yet. Questions will appear as users interact with Ask Stewie.</div>
                )}
              </div>

              {/* Weak Answers */}
              <div className="ad-card ad-card-wide" data-testid="weak-answers-table">
                <h3 className="ad-card-title"><AlertTriangle size={16} style={{display:'inline',marginRight:6,verticalAlign:'text-bottom',color:'#f59e0b'}} />Weak / Unhelpful Answers</h3>
                {weakAnswers.length > 0 ? (
                  <div className="ad-table-wrap">
                    <table className="ad-table">
                      <thead>
                        <tr><th>Question</th><th>Type</th><th>User</th><th>Time</th></tr>
                      </thead>
                      <tbody>
                        {weakAnswers.map((w, i) => (
                          <tr key={i}>
                            <td style={{maxWidth:360,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{w.questionText}</td>
                            <td><span className="ad-event-type">{w.eventType}</span></td>
                            <td>{w.userName}</td>
                            <td className="ad-time">{formatDate(w.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="ad-empty">No weak answers detected. This is a good sign!</div>
                )}
              </div>

              {/* Feature Dropoff Funnel */}
              <div className="ad-card" data-testid="feature-dropoff-chart">
                <h3 className="ad-card-title"><GitBranch size={16} style={{display:'inline',marginRight:6,verticalAlign:'text-bottom'}} />Feature Funnel (Dropoff)</h3>
                {featureDropoff.length > 0 ? (
                  <div className="ad-table-wrap">
                    <table className="ad-table">
                      <thead>
                        <tr><th>Feature</th><th>Opened</th><th>Started</th><th>Completed</th><th>Exported</th><th>Completion %</th></tr>
                      </thead>
                      <tbody>
                        {featureDropoff.map((f, i) => (
                          <tr key={i}>
                            <td>{FEATURE_LABELS[f.feature] || f.feature}</td>
                            <td><span className="ad-badge">{f.opened}</span></td>
                            <td><span className="ad-badge">{f.started}</span></td>
                            <td><span className="ad-badge">{f.completed}</span></td>
                            <td><span className="ad-badge">{f.exported}</span></td>
                            <td>
                              <div className="ad-bar-wrap">
                                <div className="ad-bar" style={{ width: `${Math.min(f.completionRate, 100)}%`, background: f.completionRate > 50 ? '#10b981' : f.completionRate > 20 ? '#f59e0b' : '#ef4444' }} />
                                <span>{f.completionRate}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="ad-empty">No feature funnel data yet.</div>
                )}
              </div>

              {/* Category Breakdown */}
              <div className="ad-card" data-testid="category-breakdown-chart">
                <h3 className="ad-card-title"><Tag size={16} style={{display:'inline',marginRight:6,verticalAlign:'text-bottom'}} />Question Category Breakdown</h3>
                {categoryBreakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={categoryBreakdown.map(c => ({ name: c.category, value: c.count }))} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {categoryBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="ad-empty">No category data yet.</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="ad-card" data-testid="top-users-table">
              <h3 className="ad-card-title">Top Users by Activity</h3>
              {topUsers.length > 0 ? (
                <div className="ad-table-wrap">
                  <table className="ad-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Events</th>
                        <th>Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topUsers.map((u, i) => (
                        <tr key={u.userId}>
                          <td>{i + 1}</td>
                          <td>{u.name}</td>
                          <td>{u.email}</td>
                          <td><span className="ad-badge">{u.eventCount}</span></td>
                          <td className="ad-time">{formatDate(u.lastActive)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="ad-empty">No user activity data yet.</div>
              )}
            </div>
          )}

          {activeTab === 'events' && (
            <div className="ad-card" data-testid="recent-events-table">
              <h3 className="ad-card-title">Recent Events</h3>
              {recentEvents.length > 0 ? (
                <div className="ad-table-wrap">
                  <table className="ad-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>User</th>
                        <th>Event</th>
                        <th>Feature</th>
                        <th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentEvents.map((e, i) => (
                        <tr key={i}>
                          <td className="ad-time">{formatDate(e.createdAt)}</td>
                          <td>{e.userName}</td>
                          <td><span className="ad-event-type">{e.eventType}</span></td>
                          <td>{FEATURE_LABELS[e.feature] || e.feature}</td>
                          <td><span className="ad-cat-badge">{e.category}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="ad-empty">No events recorded yet. Events will appear as users interact with the app.</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const dashStyles = `
  .analytics-dash {
    min-height: 100vh;
    background: #0b1120;
    color: #e2e8f0;
    padding: 24px;
    font-family: 'Inter', -apple-system, sans-serif;
  }

  .ad-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 28px;
    flex-wrap: wrap;
    gap: 16px;
  }
  .ad-header-left { display: flex; align-items: center; gap: 16px; }
  .ad-header-right { display: flex; align-items: center; gap: 12px; }
  .ad-back {
    display: flex; align-items: center; gap: 6px;
    background: #1e293b; color: #94a3b8; border: 1px solid #334155;
    padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px;
    transition: all 0.15s;
  }
  .ad-back:hover { background: #334155; color: #e2e8f0; }
  .ad-title { font-size: 24px; font-weight: 700; color: #f8fafc; margin: 0; }
  .ad-subtitle { font-size: 13px; color: #64748b; margin: 4px 0 0; }

  .ad-range-select {
    display: flex; align-items: center; gap: 6px;
    background: #1e293b; border: 1px solid #334155; border-radius: 8px; padding: 6px 10px;
  }
  .ad-range-select select {
    background: transparent; color: #e2e8f0; border: none; font-size: 13px;
    cursor: pointer; outline: none; -webkit-appearance: none;
  }
  .ad-range-select select option { background: #1e293b; }
  .ad-refresh {
    display: flex; align-items: center; gap: 6px;
    background: #3b82f6; color: white; border: none; padding: 8px 16px;
    border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
    transition: background 0.15s;
  }
  .ad-refresh:hover { background: #2563eb; }
  .ad-refresh:disabled { opacity: 0.6; }

  .ad-loading {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 300px; color: #64748b; gap: 12px;
  }

  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  /* Stat Cards */
  .ad-stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px;
    margin-bottom: 24px;
  }
  .stat-card {
    background: #111827;
    border: 1px solid #1e293b;
    border-radius: 12px;
    padding: 18px;
    display: flex;
    align-items: center;
    gap: 14px;
    transition: border-color 0.2s;
  }
  .stat-card:hover { border-color: #334155; }
  .stat-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .stat-info { display: flex; flex-direction: column; }
  .stat-value { font-size: 20px; font-weight: 700; color: #f8fafc; }
  .stat-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
  .stat-sub { font-size: 10px; color: #475569; }

  /* Tabs */
  .ad-tabs {
    display: flex; gap: 4px; margin-bottom: 20px;
    background: #111827; border-radius: 10px; padding: 4px;
    width: fit-content;
  }
  .ad-tab {
    padding: 8px 20px; border-radius: 8px; border: none;
    background: transparent; color: #64748b; cursor: pointer;
    font-size: 13px; font-weight: 500; transition: all 0.15s;
  }
  .ad-tab:hover { color: #94a3b8; }
  .ad-tab.active { background: #3b82f6; color: white; }

  /* Cards */
  .ad-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  .ad-card {
    background: #111827;
    border: 1px solid #1e293b;
    border-radius: 12px;
    padding: 20px;
  }
  .ad-card-wide { grid-column: 1 / -1; }
  .ad-card-title {
    font-size: 15px; font-weight: 600; color: #e2e8f0;
    margin: 0 0 16px;
  }
  .ad-empty {
    text-align: center; color: #475569; padding: 40px 20px; font-size: 13px;
  }

  /* KV Grid */
  .ad-kv-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .ad-kv {
    display: flex;
    flex-direction: column;
    background: #0b112080;
    border-radius: 8px;
    padding: 12px 14px;
  }
  .ad-kv-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px; }
  .ad-kv-value { font-size: 18px; font-weight: 700; color: #f8fafc; margin-top: 4px; }

  /* Bar within table */
  .ad-bar-wrap {
    display: flex; align-items: center; gap: 8px;
  }
  .ad-bar {
    height: 6px; border-radius: 3px; min-width: 4px;
  }
  .ad-bar-wrap span { font-size: 12px; color: #94a3b8; }

  /* Tables */
  .ad-table-wrap { overflow-x: auto; }
  .ad-table {
    width: 100%; border-collapse: collapse; font-size: 13px;
  }
  .ad-table th {
    text-align: left; padding: 10px 12px; color: #64748b;
    border-bottom: 1px solid #1e293b; font-weight: 500; text-transform: uppercase;
    font-size: 11px; letter-spacing: 0.5px;
  }
  .ad-table td {
    padding: 10px 12px; border-bottom: 1px solid #1e293b15; color: #cbd5e1;
  }
  .ad-table tr:hover td { background: #1e293b40; }
  .ad-badge {
    background: #3b82f620; color: #60a5fa; padding: 2px 10px;
    border-radius: 99px; font-weight: 600; font-size: 12px;
  }
  .ad-time { color: #64748b; font-size: 12px; white-space: nowrap; }
  .ad-event-type {
    background: #10b98120; color: #34d399; padding: 2px 8px;
    border-radius: 4px; font-size: 12px;
  }
  .ad-cat-badge {
    background: #f59e0b18; color: #fbbf24; padding: 2px 8px;
    border-radius: 4px; font-size: 12px;
  }

  @media (max-width: 768px) {
    .analytics-dash { padding: 16px; }
    .ad-grid-2 { grid-template-columns: 1fr; }
    .ad-header { flex-direction: column; }
    .ad-stats-grid { grid-template-columns: 1fr 1fr; }
  }
`;
